import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function DebriefPublicResponse() {
  const { slug } = useParams();
  const [debrief, setDebrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    role: "",
    what_shined: "",
    what_hurt: "",
    what_surprised: "",
    department_scores: {},
    additional_answers: {},
    free_message: "",
  });

  useEffect(() => {
    axios.get(`${API}/debriefs/public/${slug}`)
      .then((r) => {
        setDebrief(r.data);
        // Init scores at 5
        const initScores = {};
        (r.data.departments || []).forEach((d) => { initScores[d.id] = 5; });
        setForm((f) => ({ ...f, department_scores: initScores }));
      })
      .catch(() => setDebrief(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const setScore = (deptId, v) => {
    setForm((f) => ({ ...f, department_scores: { ...f.department_scores, [deptId]: Number(v) } }));
  };

  const submit = async () => {
    if (!form.what_shined.trim() || !form.what_hurt.trim() || !form.what_surprised.trim()) {
      toast.error("Les 3 questions Kaizen sont requises (court suffit)");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/debriefs/public/${slug}/respond`, form);
      toast.success("Merci. Ta voix compte.");
      setSubmitted(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center text-sm">Chargement…</div>;
  if (!debrief) {
    return (
      <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-3">KAIZEN · DEBRIEF</div>
          <h1 className="text-3xl font-bold text-zinc-100 mb-3">Debrief introuvable ou fermé</h1>
          <p className="text-zinc-500 text-sm">Le lien n'est plus actif. Contacte Kru.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-black text-zinc-200 flex items-center justify-center px-6">
        <div className="max-w-lg text-center" data-testid="debrief-thank-you">
          <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-3">RÉPONSE ENREGISTRÉE</div>
          <h1 className="text-4xl font-bold tracking-tight mb-4">Merci 🙏</h1>
          <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-2xl p-6">
            <p className="text-sm text-zinc-300">{submitted.message}</p>
          </div>
          <div className="mt-6 text-xs text-zinc-500">
            Tu peux fermer cette page. <br />
            Ton retour est <strong>strictement anonyme</strong> — aucun nom, IP ou cookie n'est lié à ta réponse.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-zinc-200" data-testid="debrief-public-page">
      <div className="border-b border-zinc-900 bg-gradient-to-br from-zinc-950 via-black to-zinc-950">
        <div className="max-w-2xl mx-auto px-6 py-10">
          <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-3">KAIZEN · DEBRIEF ANONYME</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" data-testid="event-name">{debrief.event_name}</h1>
          <div className="text-zinc-500 mt-2">{debrief.event_date}</div>
          {debrief.description && <p className="text-sm text-zinc-400 mt-3 italic">"{debrief.description}"</p>}
          <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 mt-4">🔒 100% anonyme · 5 minutes</Badge>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
        {/* Role */}
        <section>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">TON RÔLE (optionnel)</div>
          <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Ex. Sportif / Production / Bénévole / Sponsor — laisse vide si tu préfères" className="bg-zinc-950 border-zinc-800" data-testid="form-role" />
        </section>

        {/* 3 Kaizen questions */}
        <section>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">LES 3 QUESTIONS KAIZEN *</div>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">✨ 1 chose qui a vraiment bien marché</Label>
              <Textarea value={form.what_shined} onChange={(e) => setForm({ ...form, what_shined: e.target.value })} placeholder="Court et précis…" className="bg-zinc-950 border-zinc-800 min-h-[70px] mt-2" data-testid="form-shined" />
            </div>
            <div>
              <Label className="text-sm">🔥 1 chose à changer absolument</Label>
              <Textarea value={form.what_hurt} onChange={(e) => setForm({ ...form, what_hurt: e.target.value })} placeholder="Sois direct. Pas de filtre." className="bg-zinc-950 border-zinc-800 min-h-[70px] mt-2" data-testid="form-hurt" />
            </div>
            <div>
              <Label className="text-sm">🎲 1 chose qui t'a surpris (bonne ou mauvaise)</Label>
              <Textarea value={form.what_surprised} onChange={(e) => setForm({ ...form, what_surprised: e.target.value })} placeholder="L'inattendu…" className="bg-zinc-950 border-zinc-800 min-h-[70px] mt-2" data-testid="form-surprised" />
            </div>
          </div>
        </section>

        {/* Department scores */}
        <section>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">NOTE CHAQUE DÉPARTEMENT (1 = très mauvais · 10 = excellent)</div>
          <div className="space-y-3">
            {debrief.departments.map((d) => (
              <div key={d.id} className="border border-zinc-800 rounded-xl p-3 bg-zinc-950/40">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">{d.label}</Label>
                  <div className="text-2xl font-bold text-amber-300">{form.department_scores[d.id] || 5}</div>
                </div>
                <input
                  type="range" min="1" max="10"
                  value={form.department_scores[d.id] || 5}
                  onChange={(e) => setScore(d.id, e.target.value)}
                  className="w-full"
                  data-testid={`score-${d.id}`}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Free message */}
        <section>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-2">MESSAGE CONFIDENTIEL À KRU (optionnel)</div>
          <Textarea value={form.free_message} onChange={(e) => setForm({ ...form, free_message: e.target.value })} placeholder="Quelque chose que tu n'oserais pas dire en réunion ? C'est le moment." className="bg-zinc-950 border-zinc-800 min-h-[80px]" data-testid="form-free-message" />
        </section>

        <Button onClick={submit} disabled={submitting} className="w-full h-14 bg-amber-500 hover:bg-amber-400 text-black font-bold text-lg" data-testid="submit-debrief-btn">
          {submitting ? "Envoi…" : "🥋 Envoyer ma réponse anonyme"}
        </Button>

        <div className="text-center text-[11px] text-zinc-700 pb-6 border-t border-zinc-900 pt-6">
          KAIZEN System™ · Aucun nom, email, IP ou cookie n'est lié à cette réponse. Promis.
        </div>
      </div>
    </div>
  );
}
